import type { NavigateFunction } from 'react-router-dom';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import dayjs from 'dayjs';
import { FaThumbtack } from 'react-icons/fa';
import { Badge, Button } from '@/components/UI';
import { formatTimeHorizon, getEventHorizonTooltip, isEventHorizon } from '@/utils/timeHorizonGenerator.ts';
import { formatPercent, formatStrategyLabel, heatmapStyle, keyForRow, valueClass } from './helpers';
import { renderStageBadges } from './stageBadges';
import type { CompareRow } from './types';

export interface CompareHeatmapScales {
    avgProfit: number;
    medianProfit: number;
    alpha: number;
    beatSpy: number;
    optionAvg: number;
    optionMedian: number;
}

export interface BuildCompareColumnsParams {
    heatmapScales: CompareHeatmapScales;
    navigate: NavigateFunction;
    pinnedRowKeys: Set<string>;
    selectedStrategyId: number | null;
    selectedStrategyQuery: string;
    showOptionColumns: boolean;
    togglePinnedRow: (row: CompareRow) => void;
}

export function buildCompareColumns({
    heatmapScales,
    navigate,
    pinnedRowKeys,
    selectedStrategyId,
    selectedStrategyQuery,
    showOptionColumns,
    togglePinnedRow
}: BuildCompareColumnsParams): ColumnDef<CompareRow>[] {
    const baseColumns: ColumnDef<CompareRow>[] = [
        {
            id: 'pin',
            header: '',
            enableSorting: false,
            enableHiding: false,
            minSize: 56,
            size: 56,
            maxSize: 64,
            meta: { sticky: 'left', stickyOffset: 0 },
            cell: ({ row }) => {
                const rowData = row.original;
                const isPinned = pinnedRowKeys.has(keyForRow(rowData));

                return (
                    <button
                        aria-label={isPinned ? 'Unpin row' : 'Pin row'}
                        className={`inline-flex items-center justify-center rounded border px-2 py-1 text-xs transition-colors ${
                            isPinned
                                ? 'border-yellow-500 bg-yellow-500/10 text-yellow-300'
                                : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:text-white'
                        }`}
                        title={isPinned ? 'Unpin row' : 'Pin row'}
                        type="button"
                        onClick={() => togglePinnedRow(rowData)}
                    >
                        <FaThumbtack />
                    </button>
                );
            }
        },
        {
            id: 'experimentName',
            accessorFn: (row) => row.experimentName,
            header: 'Experiment',
            enableHiding: false,
            minSize: 280,
            size: 340,
            maxSize: 480,
            meta: { sticky: 'left', stickyOffset: 56 },
            cell: ({ row }) => {
                const rowData = row.original;
                const resultsUrl = `/user/experimentResults/${rowData.experimentId}${selectedStrategyQuery}`;

                return (
                    <div className="space-y-1">
                        <a
                            className="font-medium text-blue-400 hover:text-blue-300 underline"
                            href={resultsUrl}
                            onClick={(event) => {
                                event.preventDefault();
                                void navigate(resultsUrl);
                            }}
                        >
                            {rowData.experimentName}
                        </a>
                        <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                            <span>#{rowData.experimentId}</span>
                            {!selectedStrategyId && <span>{formatStrategyLabel(rowData.strategyId, rowData.strategyName)}</span>}
                            {rowData.includeOptions && <Badge variant="info">Options</Badge>}
                        </div>
                        <div className="text-xs text-zinc-500">
                            {dayjs(rowData.startDateTime).format('MMM D, YYYY')} - {dayjs(rowData.endDateTime).format('MMM D, YYYY')}
                        </div>
                        <div>{renderStageBadges(rowData)}</div>
                    </div>
                );
            }
        },
        ...(selectedStrategyId === null
            ? ([
                  {
                      id: 'strategyName',
                      accessorFn: (row) => formatStrategyLabel(row.strategyId, row.strategyName),
                      header: 'Strategy',
                      minSize: 170,
                      size: 210,
                      maxSize: 280,
                      cell: ({ row }) => (
                          <span className="text-zinc-100">{formatStrategyLabel(row.original.strategyId, row.original.strategyName)}</span>
                      )
                  }
              ] satisfies ColumnDef<CompareRow>[])
            : []),
        {
            id: 'completedAt',
            accessorFn: (row) => row.completedAt ?? '',
            header: 'Completed',
            minSize: 120,
            size: 140,
            maxSize: 160,
            cell: ({ row }) =>
                row.original.completedAt ? (
                    <span className="text-zinc-200">{dayjs(row.original.completedAt).format('MMM D, YYYY')}</span>
                ) : (
                    <span className="text-zinc-500">-</span>
                )
        },
        {
            id: 'duration',
            accessorFn: (row) => row.duration,
            header: 'Duration',
            minSize: 80,
            size: 90,
            maxSize: 100,
            cell: ({ row }) => <span className="text-zinc-100">{row.original.duration}</span>
        },
        {
            id: 'horizonSeconds',
            accessorFn: (row) => row.horizonSeconds,
            header: 'Horizon',
            minSize: 90,
            size: 110,
            maxSize: 120,
            cell: ({ row }) => {
                const tooltip = isEventHorizon(row.original.horizonSeconds)
                    ? (getEventHorizonTooltip(row.original.horizonSeconds) ?? undefined)
                    : undefined;

                return (
                    <span className="text-zinc-100" title={tooltip}>
                        {formatTimeHorizon(row.original.horizonSeconds)}
                    </span>
                );
            }
        },
        {
            id: 'totalEntries',
            accessorFn: (row) => row.metrics.totalEntries,
            header: 'Entries',
            minSize: 80,
            size: 90,
            maxSize: 100,
            cell: ({ row }) => <span className="text-zinc-100">{row.original.metrics.totalEntries.toLocaleString()}</span>
        },
        {
            id: 'totalEntities',
            accessorFn: (row) => row.metrics.totalEntities,
            header: 'Entities',
            minSize: 80,
            size: 90,
            maxSize: 100,
            cell: ({ row }) => <span className="text-zinc-100">{row.original.metrics.totalEntities.toLocaleString()}</span>
        },
        {
            id: 'avgProfitPercent',
            accessorFn: (row) => row.metrics.avgProfitPercent ?? Number.NEGATIVE_INFINITY,
            header: 'Avg Profit',
            minSize: 100,
            size: 110,
            maxSize: 130,
            cell: ({ row }) => {
                const value = row.original.metrics.avgProfitPercent;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.avgProfit)}>
                        <span className={valueClass(value)}>{formatPercent(value)}</span>
                    </div>
                );
            }
        },
        {
            id: 'medianProfitPercent',
            accessorFn: (row) => row.metrics.medianProfitPercent ?? Number.NEGATIVE_INFINITY,
            header: 'Median',
            minSize: 100,
            size: 110,
            maxSize: 130,
            cell: ({ row }) => {
                const value = row.original.metrics.medianProfitPercent;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.medianProfit)}>
                        <span className={valueClass(value)}>{formatPercent(value)}</span>
                    </div>
                );
            }
        },
        {
            id: 'avgAlphaPercent',
            accessorFn: (row) => row.metrics.avgAlphaPercent ?? Number.NEGATIVE_INFINITY,
            header: 'Alpha',
            minSize: 90,
            size: 100,
            maxSize: 120,
            cell: ({ row }) => {
                const value = row.original.metrics.avgAlphaPercent;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.alpha)}>
                        <span className={valueClass(value)}>{formatPercent(value)}</span>
                    </div>
                );
            }
        },
        {
            id: 'winRate',
            accessorFn: (row) => row.metrics.winRate ?? Number.NEGATIVE_INFINITY,
            header: 'Win Rate',
            minSize: 90,
            size: 100,
            maxSize: 120,
            cell: ({ row }) => <span className="text-zinc-100">{formatPercent(row.original.metrics.winRate, 1)}</span>
        },
        {
            id: 'winRateVsSpy',
            accessorFn: (row) => row.metrics.winRateVsSpy ?? Number.NEGATIVE_INFINITY,
            header: 'Beat SPY',
            minSize: 90,
            size: 105,
            maxSize: 120,
            cell: ({ row }) => {
                const value = row.original.metrics.winRateVsSpy;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.beatSpy)}>
                        <span className={valueClass(value)}>{formatPercent(value, 1)}</span>
                    </div>
                );
            }
        },
        {
            id: 'profitFactor',
            accessorFn: (row) => row.metrics.profitFactor ?? Number.NEGATIVE_INFINITY,
            header: 'Profit Factor',
            minSize: 100,
            size: 110,
            maxSize: 130,
            cell: ({ row }) =>
                row.original.metrics.profitFactor === null ? (
                    <span className="text-zinc-500">-</span>
                ) : (
                    <span className="text-zinc-100">{row.original.metrics.profitFactor.toFixed(2)}</span>
                )
        },
        {
            id: 'coefficientOfVariation',
            accessorFn: (row) => row.metrics.coefficientOfVariation ?? Number.NEGATIVE_INFINITY,
            header: 'CV',
            minSize: 80,
            size: 90,
            maxSize: 100,
            cell: ({ row }) =>
                row.original.metrics.coefficientOfVariation === null ? (
                    <span className="text-zinc-500">-</span>
                ) : (
                    <span className="text-zinc-100">{row.original.metrics.coefficientOfVariation.toFixed(2)}</span>
                )
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            enableHiding: false,
            minSize: 150,
            size: 170,
            maxSize: 200,
            cell: ({ row }) => {
                const rowData = row.original;
                const resultsUrl = `/user/experimentResults/${rowData.experimentId}${selectedStrategyQuery}`;

                const entitiesUrl = `/user/experimentResults/${rowData.experimentId}/entities?duration=${rowData.duration}${
                    selectedStrategyId ? `&strategyId=${selectedStrategyId}` : ''
                }`;

                return (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline-dark" onClick={() => navigate(resultsUrl)}>
                            Results
                        </Button>
                        <Button size="sm" variant="outline-dark" onClick={() => navigate(entitiesUrl)}>
                            Entities
                        </Button>
                    </div>
                );
            }
        }
    ];

    if (!showOptionColumns) {
        return baseColumns;
    }

    const actionColumnIndex = baseColumns.findIndex((column) => column.id === 'actions');
    const optionColumnInsertIndex = actionColumnIndex >= 0 ? actionColumnIndex : baseColumns.length;

    return [
        ...baseColumns.slice(0, optionColumnInsertIndex),
        {
            id: 'optionAvgProfitPercent',
            accessorFn: (row) => row.metrics.optionAvgProfitPercent ?? Number.NEGATIVE_INFINITY,
            header: 'Option Avg',
            minSize: 100,
            size: 115,
            maxSize: 130,
            cell: ({ row }) => {
                const value = row.original.metrics.optionAvgProfitPercent;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.optionAvg)}>
                        <span className={valueClass(value)}>{formatPercent(value)}</span>
                    </div>
                );
            }
        },
        {
            id: 'optionMedianProfitPercent',
            accessorFn: (row) => row.metrics.medianOptionProfitPercent ?? Number.NEGATIVE_INFINITY,
            header: 'Option Median',
            minSize: 110,
            size: 125,
            maxSize: 140,
            cell: ({ row }) => {
                const value = row.original.metrics.medianOptionProfitPercent;

                return (
                    <div className="-mx-3 -my-3 px-3 py-3" style={heatmapStyle(value, heatmapScales.optionMedian)}>
                        <span className={valueClass(value)}>{formatPercent(value)}</span>
                    </div>
                );
            }
        },
        {
            id: 'optionWinRate',
            accessorFn: (row) => row.metrics.optionWinRate ?? Number.NEGATIVE_INFINITY,
            header: 'Option Win',
            minSize: 100,
            size: 110,
            maxSize: 125,
            cell: ({ row }) => <span className="text-zinc-100">{formatPercent(row.original.metrics.optionWinRate, 1)}</span>
        },
        ...baseColumns.slice(optionColumnInsertIndex)
    ];
}
